import { setAccessToken } from '@driftt/web-auth'
import ActionConstants from 'constants/ActionConstants'
import { notifyAcceptedInvitation } from 'utils/NotificationHelper'
import UserConstants from 'constants/UserConstants'
import { resolvePasswordErrorMessage } from 'utils/passwordUtils'
import { getRavenClient } from 'utils/SentryHelpers'
import { identifyUser } from 'utils/tracking'
import { pick } from 'ramda'

const UserStore = Marty.createStore({
  id: 'UserStore',

  getInitialState() {
    return {
      usersById: {},
      userExistenceByOrgHandle: {},
      userExistenceByEmail: {},
      emailsAlreadyRegistered: {},
      userUpdateState: {},
      passwordChangeState: {},
      passwordErrorMessage: '',
      oAuthCodes: {},
      oAuthAppInfo: {},
      oAuthFailures: {}
    }
  },

  handlers: {
    refreshUsers: ActionConstants.REFRESH_ALL_PLZ,
    fetchUserDone: UserConstants.USER_FETCH_DONE,
    fetchUsersForOrgIdDone: UserConstants.FETCH_USERS_FOR_ORG_ID_DONE,
    fetchCurrentUserDone: UserConstants.FETCH_CURRENT_USER_DONE,

    createUserDone: UserConstants.USER_CREATE_DONE,
    createUserWithEmailDone: UserConstants.USER_CREATE_WITH_EMAIL_DONE,
    emailAlreadyRegistered: UserConstants.EMAIL_ALREADY_REGISTERED,

    checkUserExistenceByOrgHandle: UserConstants.CHECK_USER_EXISTENCE_BY_ORG_HANDLE_AND_EMAIL,
    checkUserExistenceByOrgHandleDone: UserConstants.CHECK_USER_EXISTENCE_BY_ORG_HANDLE_AND_EMAIL_DONE,
    checkUserExistenceByEmailDone: UserConstants.CHECK_USER_EXISTENCE_BY_EMAIL_DONE,

    updateUser: UserConstants.USER_UPDATE,
    updateUserDone: UserConstants.USER_UPDATE_DONE,

    changePassword: UserConstants.CHANGE_PASSWORD,
    changePasswordDone: UserConstants.CHANGE_PASSWORD_DONE,
    changePasswordFailed: UserConstants.CHANGE_PASSWORD_FAILED,
    clearPasswordValidationErrors: UserConstants.CLEAR_PASSWORD_VALIDATION_ERRORS,
    uploadUserAvatar: UserConstants.USER_AVATAR_UPLOAD,

    archiveUser: UserConstants.USER_ARCHIVE,
    archiveUserDone: UserConstants.USER_ARCHIVE_DONE,
    archiveUserFailed: UserConstants.USER_ARCHIVE_FAILED,

    archiveUsers: UserConstants.ARCHIVE_USERS,
    archiveUsersDone: UserConstants.ARCHIVE_USERS_DONE,
    archiveUsersFailed: UserConstants.ARCHIVE_USERS_FAILED,

    fetchOAuthCodeDone: UserConstants.FETCH_OAUTH_CODE_DONE,
    fetchOAuthCodeFailed: UserConstants.FETCH_OAUTH_CODE_FAILED,

    fetchOauthAppInfoDone: UserConstants.FETCH_OAUTH_APP_DONE
  },

  refreshUsers() {
    return this.app.queries.UserQueries.fetchUsersForOrgId(this.getCurrentUser().orgId)
  },

  emailAlreadyRegistered(email) {
    return this.setEmailAlreadyRegistered(email)
  },

  getEmailsAlreadyRegistered() {
    return this.state.emailsAlreadyRegistered
  },

  setEmailAlreadyRegistered(email, registered) {
    if (registered == null) { registered = true }
    const emailsAlreadyRegistered = _.cloneDeep(this.state.emailsAlreadyRegistered)
    emailsAlreadyRegistered[email] = registered
    return this.setState({ emailsAlreadyRegistered })
  },

  getUsersById() {
    return this.state.usersById
  },

  getOrFetchUser(userId) {
    return this.fetch({
      id: `fetchUser${ userId }`,
      locally() {
        return this.state.usersById[userId]
      },
      remotely() {
        return this.app.queries.UserQueries.fetchUser(userId)
      }
    })
  },

  getOrFetchUsersForCurrentUserOrg() {
    return this.fetch({
      id: 'getOrFetchUsersForCurrentUserOrg',
      dependsOn: [this.getOrFetchCurrentUser()],
      locally() {
        if (this.hasAlreadyFetched('getOrFetchUsersForCurrentUserOrg')) {
          return this.getUsersForOrgId(this.getCurrentUser().orgId)
        }
      },
      remotely() {
        return this.app.queries.UserQueries.fetchUsersForOrgId(this.getCurrentUser().orgId)
      }
    })
  },

  getOrFetchOnCallUsersForCurrentUserOrg() {
    return this.fetch({
      id: 'getOrFetchOnCallUsersForCurrentUserOrg',
      dependsOn: [this.getOrFetchUsersForCurrentUserOrg()],
      locally() {
        return this.getOnCallUsersForOrgId(this.getCurrentUser().orgId)
      }
    })
  },

  getOrFetchActiveUsersForCurrentUserOrg() {
    return this.fetch({
      id: 'getOrFetchActiveUsersForCurrentUserOrg',
      dependsOn: [this.getOrFetchUsersForCurrentUserOrg()],
      locally() {
        return this.getActiveUsersForOrgId(this.getCurrentUser().orgId)
      }
    })
  },

  getOrFetchNonBotUsersForCurrentUserOrg() {
    return this.fetch({
      id: 'getOrFetchNonBotUsersForCurrentUserOrg',
      dependsOn: [this.getOrFetchUsersForCurrentUserOrg()],
      locally() {
        return this.getNonBotUsersForOrgId(this.getCurrentUser().orgId)
      }
    })
  },

  getUsersForOrgId(orgId) {
    return _.pickBy(this.state.usersById, user => user.orgId === orgId)
  },

  getOnCallUsersForOrgId(orgId) {
    return _.pickBy(this.state.usersById, user => (user.orgId === orgId) && (user.availability === 'ON_CALL'))
  },

  getActiveUsersForOrgId(orgId) {
    return _.pickBy(this.state.usersById, user => (user.orgId === orgId) && (user.status === 'ENABLED'))
  },

  getNonBotUsersForOrgId(orgId) {
    return _.pickBy(this.state.usersById, user => (user.orgId === orgId) && !user.bot)
  },

  fetchUserDone(userId, user) {
    const usersById = _.cloneDeep(this.state.usersById)
    usersById[userId] = user
    return this.setState({ usersById })
  },

  fetchUsersForOrgIdDone(orgId, users) {
    const usersById = _.cloneDeep(this.state.usersById)
    _.each(users, user => usersById[user.id] = user)
    return this.setState({ usersById })
  },

  getOrFetchCurrentUser() {
    return this.fetch({
      id: 'getCurrentUser',
      cacheError: false,
      locally() {
        return this.getCurrentUser()
      },
      remotely() {
        return this.app.queries.UserQueries.fetchCurrentLoggedInUser()
      }
    })
  },

  fetchOauthCode({ redirect_uri, client_id }) {
    const id = `OauthCode${ JSON.stringify({ redirect_uri, client_id }) }`
    return this.fetch({
      id,
      locally() {
        if (this.hasAlreadyFetched(id)) {
          return this.state.oAuthCodes[id]
        }
      },
      remotely() {
        return this.app.queries.UserQueries.fetchOauthCode({ redirect_uri, client_id })
      }
    })
  },

  fetchOAuthCodeDone({ redirect_uri, client_id, body }) {
    const id = this.getOAuthId({ redirect_uri, client_id })
    const oAuthCodes = _.cloneDeep(this.state.oAuthCodes)
    oAuthCodes[id] = body.code
    return this.setState({ oAuthCodes })
  },

  getOAuthId({ redirect_uri, client_id }) {
    return `OauthCode${ JSON.stringify({ redirect_uri, client_id }) }`
  },

  fetchOAuthCodeFailed({ redirect_uri, client_id, body }) {
    const id = this.getOAuthId({ redirect_uri, client_id })
    const oAuthFailures = _.cloneDeep(this.state.oAuthFailures)
    oAuthFailures[id] = body
    return this.setState({ oAuthFailures })
  },

  getOAuthFailures({ redirect_uri, client_id }) {
    const id = this.getOAuthId({ redirect_uri, client_id })
    return this.state.oAuthFailures[id]
  },

  fetchOauthAppInfo(client_id) {
    const id = `fetchOauthApp${ client_id }`
    return this.fetch({
      id,
      locally() {
        if (this.hasAlreadyFetched(id)) {
          return this.state.oAuthAppInfo[client_id]
        }
      },
      remotely() {
        return this.app.queries.UserQueries.fetchOauthAppInfo(client_id)
      }
    })
  },

  fetchOauthAppInfoDone({ client_id, app }) {
    const oAuthAppInfo = _.cloneDeep(this.state.oAuthAppInfo)
    oAuthAppInfo[client_id] = app
    return this.setState({ oAuthAppInfo })
  },

  fetchCurrentUserDone(user) {
    const ravenClient = getRavenClient()
    if (ravenClient != null) {
      ravenClient.setUserContext(user)
    }
    if (Marty.isBrowser) {
      identifyUser(user)
    }
    this.fetchUserDone(user.id, user)
    return this.setState({ currentUserId: user.id })
  },

  setCurrentUser(email, user, accessToken) {
    this.setEmailAlreadyRegistered(email)
    this.fetchCurrentUserDone(user)
    return setAccessToken({ token: accessToken.accessToken })
  },

  createUserDone(email, user, accessToken) {
    return this.setCurrentUser(email, user, accessToken)
  },

  createUserWithEmailDone(email, user, accessToken) {
    return this.setCurrentUser(email, user, accessToken)
  },

  getCurrentUser() {
    return this.state.usersById[this.state.currentUserId]
  },

  fetchUserExistenceByEmail(email) {
    return this.fetch({
      id: `fetchUserExistenceByEmail${email}`,
      locally() {
        return this.state.userExistenceByEmail[email]
      },
      remotely() {
        return this.app.queries.UserQueries.checkUserEmailExistence(email)
      }
    })
  },

  getUserExistenceByEmail() {
    return this.state.userExistenceByEmail || {}
  },

  checkUserExistenceByEmailDone(email, exists) {
    const userExistenceByEmail = _.cloneDeep(this.state.userExistenceByEmail)
    userExistenceByEmail[email] = exists
    return this.setState({ userExistenceByEmail })
  },

  checkUserExistenceByOrgHandle(email, orgHandle) {
    if (((this.state.userExistenceByOrgHandle[orgHandle] != null ? this.state.userExistenceByOrgHandle[orgHandle][email] : undefined) == null)) {
      return this.app.queries.UserQueries.getUserExistenceByEmailAndOrgHandle(email, orgHandle)
    }
  },

  checkUserExistenceByOrgHandleDone(email, orgHandle, exists) {
    const userExistenceByOrgHandle = _.cloneDeep(this.state.userExistenceByOrgHandle)
    userExistenceByOrgHandle[orgHandle] = userExistenceByOrgHandle[orgHandle] || {}
    userExistenceByOrgHandle[orgHandle][email] = exists
    return this.setState({ userExistenceByOrgHandle })
  },

  getUserExistenceByOrgHandle() {
    return this.state.userExistenceByOrgHandle
  },

  // Update User

  updateUser(userId, params) {
    this.app.queries.UserQueries.updateUser({
      userId,
      params,
      isCurrentUser: this.state.currentUserId === userId,
      notify: true
    })
    const userUpdateState = _.cloneDeep(this.state.userUpdateState)
    userUpdateState[userId] = UserConstants.USER_UPDATE_STARTING
    return this.setState({
      userUpdateState
    })
  },

  updateUserDone(userId, user) {
    const usersById = _.cloneDeep(this.state.usersById)
    const oldUser = usersById[userId]
    if ((user.status === 'ENABLED') && ((oldUser != null ? oldUser.status : undefined) === 'INVITED')) {
      notifyAcceptedInvitation(user)
    }

    usersById[userId] = user
    const userUpdateState = _.cloneDeep(this.state.userUpdateState)
    userUpdateState[userId] = UserConstants.USER_UPDATE_DONE
    return this.setState({
      userUpdateState,
      usersById
    })
  },

  changePassword(userId, password) {
    this.app.queries.UserQueries.changePassword(userId, password)
    const passwordChangeState = _.cloneDeep(this.state.passwordChangeState)
    passwordChangeState[userId] = UserConstants.CHANGE_PASSWORD_STARTING
    return this.setState({
      passwordChangeState
    })
  },

  changePasswordDone(userId, response) {
    setAccessToken({ token: response.access_token })
    const passwordChangeState = _.cloneDeep(this.state.passwordChangeState)
    passwordChangeState[userId] = UserConstants.CHANGE_PASSWORD_DONE
    return this.setState({
      passwordChangeState,
      passwordErrorMessage: ''
    })
  },

  changePasswordFailed(userId, validationErrors) {
    let passwordErrorMessage = ''
    if (validationErrors && validationErrors[0]) {
      passwordErrorMessage = resolvePasswordErrorMessage(validationErrors[0].message)
    }

    const passwordChangeState = _.cloneDeep(this.state.passwordChangeState)
    passwordChangeState[userId] = UserConstants.CHANGE_PASSWORD_FAILED
    return this.setState({
      passwordChangeState,
      passwordErrorMessage
    })
  },

  clearPasswordValidationErrors() {
    return this.setState({
      passwordErrorMessage: ''
    })
  },

  getPasswordChangeState() {
    return this.state.passwordChangeState
  },

  getPasswordErrorMessage() {
    return this.fetch({
      id: 'getPasswordErrorMessage',
      locally() {
        return this.state.passwordErrorMessage
      },
      remotely() {
        return this.state.passwordErrorMessage
      }
    })
  },

  resetPasswordChangeState() {
    return this.setState({ passwordChangeState: {}})
  },

  getUserUpdateState() {
    return this.state.userUpdateState
  },

  resetUserUpdateState() {
    return this.setState({ userUpdateState: {}})
  },

  uploadUserAvatar(userId, file) {
    const userUpdateState = _.cloneDeep(this.state.userUpdateState)
    userUpdateState[userId] = UserConstants.USER_UPDATE_STARTING
    this.setState({
      userUpdateState
    })
    return this.app.queries.UserQueries.uploadUserAvatar(userId, file)
  },

  archiveUser(userId) {
    return this.app.queries.UserQueries.archiveUser(userId)
  },

  archiveUserDone(userId) {
    const usersById = _.cloneDeep(this.state.usersById)
    delete usersById[userId]
    return this.setState({ usersById })
  },

  archiveUserFailed() {
  },

  archiveUsers(userIds) {
    return this.app.queries.UserQueries.archiveUsers(userIds)
  },

  archiveUsersFailed() {
  },

  archiveUsersDone(userIds) {
    return pick(userIds, this.state.usersById)
  },

  onHydrate() {
    const currentUser = this.getCurrentUser()
    if (currentUser) {
      const ravenClient = getRavenClient()
      if (ravenClient != null) {
        ravenClient.setUserContext(currentUser)
      }
      return SegmentHelpers.identifyUser(currentUser)
    }
  }
})

export default UserStore



// WEBPACK FOOTER //
// ./src/scripts/stores/UserStore.js